import { useMemo } from 'react';

/**
 * useCashSummary - คำนวณสรุปเงินสด + ต้นทุนเฉลี่ย USD/THB
 * 
 * DEPOSIT = ซื้อ USD (THB → USD) → เพิ่ม USD balance
 * WITHDRAW = ขาย USD (USD → THB) → ลด USD balance
 * 
 * ต้นทุนเฉลี่ย = ถัวเฉลี่ยถ่วงน้ำหนักจาก DEPOSIT ทั้งหมด
 * Realized FX P&L = กำไร/ขาดทุนจาก WITHDRAW (ราคาขาย - ต้นทุนเฉลี่ย) × จำนวน USD
 * Unrealized FX P&L = (อัตราปัจจุบัน - ต้นทุนเฉลี่ย) × USD คงเหลือ
 */
export const useCashSummary = (transactions, currentExchangeRate) => {
  return useMemo(() => {
    const cashTx = transactions.filter(t => t.type === 'DEPOSIT' || t.type === 'WITHDRAW');
    const stockTx = transactions.filter(t => t.type === 'BUY' || t.type === 'SELL' || t.type === 'DIVIDEND');
    const adjustTx = transactions.filter(t => t.type === 'CASH_ADJUST');

    // === ส่วน 1: FIFO-style ต้นทุนเฉลี่ย USD/THB ===
    let totalDepositTHB = 0;
    let totalDepositUSD = 0;
    let totalWithdrawTHB = 0;
    let totalWithdrawUSD = 0;
    let depositCount = 0;
    let withdrawCount = 0;

    // เรียงตาม date ก่อน → หลัง แล้วตามด้วย createdAt ลำดับเพื่อรักษาสถานะ FIFO ในวันเดียวกัน
    const sortedCashTx = [...cashTx].sort((a, b) => {
      const d1 = new Date(a.date).getTime();
      const d2 = new Date(b.date).getTime();
      if (d1 !== d2) return d1 - d2;
      return (a.createdAt || 0) - (b.createdAt || 0);
    });

    // คำนวณ Weighted Average Cost (ถัวเฉลี่ยถ่วงน้ำหนัก)
    // เมื่อ DEPOSIT: avgCost = (เดิม × USD เดิม + ใหม่ THB) ÷ (USD เดิม + USD ใหม่)
    // เมื่อ WITHDRAW: ใช้ avgCost ณ ปัจจุบัน → คำนวณ realized P&L
    let runningUSD = 0;
    let runningCostTHB = 0; // ต้นทุนรวม THB ของ USD ที่ถืออยู่
    let realizedFxPnL = 0;

    const fxHistory = []; // ประวัติ FX transactions

    sortedCashTx.forEach(t => {
      const thb = parseFloat(t.amountTHB || 0);
      const rate = parseFloat(t.exchangeRate || 0);
      if (rate <= 0) return;

      const usd = thb / rate;

      if (t.type === 'DEPOSIT') {
        // ซื้อ USD: เพิ่ม balance + ปรับ weighted average
        totalDepositTHB += thb;
        totalDepositUSD += usd;
        depositCount++;

        runningCostTHB += thb; // ต้นทุน THB เพิ่มขึ้น
        runningUSD += usd;     // USD เพิ่มขึ้น

        fxHistory.push({
          date: t.date,
          type: 'DEPOSIT',
          amountTHB: thb,
          amountUSD: usd,
          rate,
          avgCostAfter: runningUSD > 0 ? runningCostTHB / runningUSD : 0,
          note: t.note
        });
      } else if (t.type === 'WITHDRAW') {
        // ขาย USD: ลด balance + คำนวณ realized FX P&L
        totalWithdrawTHB += thb;
        totalWithdrawUSD += usd;
        withdrawCount++;

        // ต้นทุนเฉลี่ย ณ ตอนขาย
        const avgCostAtSell = runningUSD > 0 ? runningCostTHB / runningUSD : 0;

        // Realized P&L = (ราคาขาย - ต้นทุน) × USD
        // ราคาขาย = rate (THB/USD ที่ขายได้)
        // ถ้า rate > avgCost → กำไร (USD แข็งค่า ขายได้บาทมากกว่าที่ซื้อ)
        // ถ้า rate < avgCost → ขาดทุน
        const fxPnL = (rate - avgCostAtSell) * usd;
        realizedFxPnL += fxPnL;

        // ลดต้นทุนตามสัดส่วน
        const costReduced = avgCostAtSell * usd;
        runningCostTHB = Math.max(0, runningCostTHB - costReduced);
        runningUSD = Math.max(0, runningUSD - usd);

        fxHistory.push({
          date: t.date,
          type: 'WITHDRAW',
          amountTHB: thb,
          amountUSD: usd,
          rate,
          avgCostAtSell,
          fxPnL,
          avgCostAfter: runningUSD > 0 ? runningCostTHB / runningUSD : 0,
          note: t.note
        });
      }
    });

    // ต้นทุนเฉลี่ยปัจจุบัน
    const avgCostRate = runningUSD > 0 ? runningCostTHB / runningUSD : 0;

    // === ส่วน 2: Cash Balance (USD คงเหลือในพอร์ต) ===
    // Smart Shield: ข้ามสินทรัพย์ที่ซื้อด้วยเงินบาท (เช่น GOLDTHB) 
    // เพื่อไม่ให้กระทบกระสุน USD ที่ตุนไว้เทรดหุ้นอเมริกา
    const isTHBAsset = (symbol) => {
      if (!symbol) return false;
      const upper = symbol.toUpperCase();
      return upper.endsWith('THB') || upper === 'GOLDTH';
    };

    let totalBuyUSD = 0;
    let totalSellUSD = 0;
    let totalDividendUSD = 0;

    stockTx.forEach(t => {
      if (isTHBAsset(t.symbol)) return; // ข้ามสินทรัพย์ THB
      const amount = t.price * t.quantity;
      const fee = parseFloat(t.feeTax || 0);
      if (t.type === 'BUY') {
        totalBuyUSD += amount + fee; // ซื้อหุ้น ต้องจ่ายค่าหุ้น + จ่ายค่าธรรมเนียม
      } else if (t.type === 'SELL') {
        totalSellUSD += amount - fee; // ขายหุ้น ได้เงินกลับมา - โดนหักค่าธรรมเนียม
      } else if (t.type === 'DIVIDEND') {
        totalDividendUSD += amount - fee; // ได้ปันผล - โดนหักภาษี
      }
    });

    // === ส่วน 2.5: Cash Adjustments ===
    let totalAdjustUSD = 0;
    adjustTx.forEach(t => {
      totalAdjustUSD += parseFloat(t.amountUSD || 0);
    });

    const cashBalanceUSD = totalDepositUSD - totalWithdrawUSD - totalBuyUSD + totalSellUSD + totalDividendUSD + totalAdjustUSD;

    // === ส่วน 3: Unrealized FX P&L ===
    // (อัตราปัจจุบัน - ต้นทุนเฉลี่ย) × USD ที่ฝากเข้ามาทั้งหมดที่ยังเหลืออยู่ (net deposit)
    const netDepositUSD = runningUSD; // USD จาก deposit ที่ยังไม่ถูก withdraw
    const unrealizedFxPnL = currentExchangeRate && avgCostRate > 0
      ? (currentExchangeRate - avgCostRate) * netDepositUSD
      : 0;

    // FX P&L per USD
    const fxPnLPerUSD = currentExchangeRate && avgCostRate > 0
      ? currentExchangeRate - avgCostRate
      : 0;

    const fxPnLPercent = avgCostRate > 0
      ? ((currentExchangeRate - avgCostRate) / avgCostRate) * 100
      : 0;

    return {
      // เงินฝาก/ถอน
      totalDepositTHB,
      totalDepositUSD,
      totalWithdrawTHB,
      totalWithdrawUSD,
      depositCount,
      withdrawCount,

      // ยอด USD ยังเหลืออยู่
      netDepositUSD,    // USD จาก deposit ที่ยังไม่ถูก withdraw
      cashBalanceUSD,   // USD คงเหลือหลังหักซื้อ/ขายหุ้น

      // ต้นทุนเฉลี่ย FX
      avgCostRate,      // ต้นทุนเฉลี่ย THB/USD (ถัวเฉลี่ยถ่วงน้ำหนัก)

      // FX P&L
      fxPnLPerUSD,      // กำไร/ขาดทุนต่อ 1 USD
      fxPnLPercent,     // % เปลี่ยนแปลง
      realizedFxPnL,    // Realized FX P&L (จาก WITHDRAW)
      unrealizedFxPnL,  // Unrealized FX P&L (USD ที่ยังถืออยู่)
      totalFxPnL: realizedFxPnL + unrealizedFxPnL,

      // Stock trading
      totalBuyUSD,
      totalSellUSD,
      totalDividendUSD,
      totalAdjustUSD,

      // ประวัติ
      fxHistory
    };
  }, [transactions, currentExchangeRate]);
};
